// components/VerificationHistoryModal.js
import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  ArrowTopRightOnSquareIcon, 
  CheckCircleIcon, 
  XCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  BeakerIcon,
  DocumentDuplicateIcon,
  ArrowPathIcon
} from '@heroicons/react/24/solid';

export function VerificationHistoryModal({ isOpen, onClose }) {
  const [spins, setSpins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSpin, setSelectedSpin] = useState(null);
  const [verificationResult, setVerificationResult] = useState(null);
  const [verifyingInProgress, setVerifyingInProgress] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 1,
    limit: 10
  });

  // Load spin history when modal opens or page changes
  useEffect(() => {
    if (isOpen) {
      fetchSpinHistory();
    }
  }, [isOpen, page]);

  const fetchSpinHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      if (!token) {
        setError("Authentication required");
        return;
      }
      
      console.log(`Fetching spin history, page ${page}, limit 10`);
      
      const response = await axios.get(`/api/casino/quantum/history?page=${page}&limit=10`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log("History API response:", response.data);
      
      if (response.data.success) {
        // Process the data to ensure all fields exist
        const processedSpins = response.data.data.map(spin => ({
          ...spin,
          // Ensure these fields exist with default values if missing
          _id: spin.id || spin._id, // Handle both formats
          amount: parseFloat(spin.betAmount || spin.amount || 0),
          payout: parseFloat(spin.payout || 0),
          result: spin.result || 'unknown',
          createdAt: spin.timestamp || spin.date || spin.createdAt || new Date().toISOString(),
          verificationHash: spin.verificationHash || null,
          verificationUrl: spin.verificationUrl || null,
          quantumSource: spin.quantumSource || 'Unknown',
          quantumRandomness: spin.quantumRandomness || null,
          requestId: spin.requestId || null,
          isVerified: spin.isVerified || false
        }));
        
        setSpins(processedSpins);
        setPagination({
          total: response.data.pagination.total || 0,
          totalPages: response.data.pagination.totalPages || 1,
          limit: response.data.pagination.limit || 10
        });
      } else {
        setError("Failed to load spin history");
      }
    } catch (err) {
      console.error("Error fetching spin history:", err);
      setError(err.response?.data?.error || "Error loading history");
    } finally {
      setLoading(false);
    }
  };

  // Verify a specific spin
  const verifySpin = async (spin) => {
    try {
      setVerifyingInProgress(true);
      setError(null);
      setVerificationResult(null);
      
      const token = localStorage.getItem('token');
      if (!token) {
        setError("Authentication required");
        return;
      }
      
      // Determine what parameter to use for verification
      let queryParam;
      
      if (spin.requestId) {
        queryParam = `id=${encodeURIComponent(spin.requestId)}`;
      } else if (spin.verificationHash) {
        queryParam = `hash=${encodeURIComponent(spin.verificationHash)}`;
      } else if (spin.id || spin._id) {
        queryParam = `id=${encodeURIComponent(spin.id || spin._id)}`;
      } else {
        setError("No verification parameters available");
        setVerifyingInProgress(false);
        return;
      }
      
      console.log(`Verifying spin with query: ${queryParam}`);
      
      const response = await axios.get(`/api/casino/quantum/verify?${queryParam}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log("Verification API response:", response.data);
      
      if (response.data.success && response.data.verified) {
        // Create a verification result object
        const verData = response.data.data;
        
        const verificationResultObj = {
          isVerified: true,
          data: verData,
          gridVerified: !!verData.grid,
          payoutVerified: parseFloat(verData.payout) === parseFloat(spin.payout),
          timestamp: verData.timestamp || null,
          quantumRandomness: verData.quantumData?.randomValues || null
        };
        
        setVerificationResult(verificationResultObj);
        
        // Update selected spin with more complete data
        const updatedSpin = {
          ...spin,
          ...verData,
          quantumRandomness: verData.quantumData?.randomValues || spin.quantumRandomness,
          isVerified: true
        };
        
        setSelectedSpin(updatedSpin);
        
        // Update the spin in the spins array to reflect verification status
        const updatedSpins = spins.map(s => 
          (s._id === spin._id || s.id === spin.id || s.requestId === spin.requestId) 
            ? updatedSpin 
            : s
        );
        
        setSpins(updatedSpins);
      } else {
        setVerificationResult({
          isVerified: false,
          error: response.data.error || "Verification failed"
        });
      }
    } catch (err) {
      console.error("Error verifying spin:", err);
      setVerificationResult({
        isVerified: false,
        error: err.response?.data?.error || "Error during verification"
      });
      setError(err.response?.data?.error || "Error during verification");
    } finally {
      setVerifyingInProgress(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      return date.toLocaleString();
    } catch (e) {
      return 'Invalid Date';
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '$0.00';
    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount)) return '$0.00';
    return `$${parsedAmount.toFixed(2)}`;
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      // Could add a temporary notification here
    }).catch(err => {
      console.error('Failed to copy: ', err);
    });
  };

  // Navigate through pages
  const nextPage = () => {
    if (page < pagination.totalPages) {
      setPage(page + 1);
    }
  };

  const prevPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  // Clear verification state when closing detail view
  const handleBackToList = () => {
    setSelectedSpin(null);
    setVerificationResult(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-95 z-50 flex flex-col items-center justify-center p-2 sm:p-4 overflow-auto">
      <div className="bg-gray-900/80 backdrop-blur-sm rounded-lg p-3 sm:p-4 w-full max-w-2xl mx-auto border border-gray-800 max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex justify-between items-center mb-3 sm:mb-4 sticky top-0 bg-gray-900/90 p-1 z-10">
          <h2 className="text-lg sm:text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-400 to-indigo-400">
            {selectedSpin ? 'Spin Verification' : 'Quantum Spin History'}
          </h2>
          <button 
            onClick={selectedSpin ? handleBackToList : onClose}
            className="text-gray-300 hover:text-white bg-gray-800 p-1.5 sm:p-2 rounded-full"
          >
            {selectedSpin ? (
              <ChevronLeftIcon className="h-5 w-5" />
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
          </button>
        </div>
        
        {/* Error message */}
        {error && (
          <div className="bg-red-900/30 border border-red-700 text-red-300 p-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}
        
        {/* Loading state */}
        {loading && (
    <div className="min-h-screen text-white font-mono flex items-center justify-center">
    <div className="w-full max-w-lg mx-auto shadow-lg p-6 text-center">
      <div className="flex flex-col items-center">
        <div className="w-16 h-16 border-2 border-violet-500 rounded-full relative flex items-center justify-center">
          <div className="w-10 h-10 bg-gradient-to-r from-violet-500 via-pink-500 to-red-500 rounded-full animate-pulse"></div>
          <div className="absolute inset-0 border-t-2 border-violet-500 rounded-full animate-spin"></div>
        </div>
        <div className="mt-4 font-mono text-sm">
          <span className="">
            Loading Spin History...
          </span>
        </div>
      </div>
    </div>
  </div>
        )}
        
        {/* Spin detail view */}
        {selectedSpin && (
          <div className="space-y-4">
            <div className="bg-gray-800/40 p-4 rounded-lg border border-gray-700">
              <h3 className="text-sm font-semibold text-cyan-400 mb-3">Spin Details</h3>
              
              <div className="grid grid-cols-2 gap-2 mb-4">
                <div className="bg-gray-800/80 p-2 rounded border border-gray-700">
                  <div className="text-xs text-gray-400">Request ID</div>
                  <div className="text-sm text-gray-200 truncate">{selectedSpin.requestId || 'N/A'}</div>
                </div>
                
                <div className="bg-gray-800/80 p-2 rounded border border-gray-700">
                  <div className="text-xs text-gray-400">Date & Time</div>
                  <div className="text-sm text-gray-200">{formatDate(selectedSpin.timestamp || selectedSpin.createdAt)}</div>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="bg-gray-800/80 p-2 rounded border border-gray-700 text-center">
                  <div className="text-xs text-gray-400">Bet Amount</div>
                  <div className="text-sm font-medium text-white">{formatCurrency(selectedSpin.amount || selectedSpin.betAmount)}</div>
                </div>
                
                <div className="bg-gray-800/80 p-2 rounded border border-gray-700 text-center">
                  <div className="text-xs text-gray-400">Result</div>
                  <div className={`text-sm font-medium ${selectedSpin.result === 'win' ? 'text-green-400' : 'text-red-400'}`}>
                    {selectedSpin.result === 'win' ? 'WIN' : 'LOSE'}
                  </div>
                </div>
                
                <div className="bg-gray-800/80 p-2 rounded border border-gray-700 text-center">
                  <div className="text-xs text-gray-400">Payout</div>
                  <div className={`text-sm font-medium ${parseFloat(selectedSpin.payout) > 0 ? 'text-green-400' : 'text-gray-300'}`}>
                    {formatCurrency(selectedSpin.payout)}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-400">Quantum Source:</span>
                <span className="text-xs text-cyan-400">{selectedSpin.quantumSource || 'Unknown'}</span>
              </div>
              
              {/* Verification result */}
              {verificationResult && (
                <div className={`mt-4 p-3 rounded-lg border ${
                  verificationResult.isVerified 
                    ? "bg-green-900/20 border-green-800" 
                    : "bg-red-900/20 border-red-800"
                }`}>
                  <div className="flex items-center mb-2">
                    {verificationResult.isVerified ? (
                      <>
                        <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                        <span className="text-sm font-medium text-green-400">Verified Successfully</span>
                      </>
                    ) : (
                      <>
                        <XCircleIcon className="h-5 w-5 text-red-500 mr-2" />
                        <span className="text-sm font-medium text-red-400">Verification Failed</span>
                      </>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-y-1 gap-x-3 text-xs">
                    <div className="text-gray-400">Hash Verification:</div>
                    <div className={verificationResult.isVerified ? "text-green-400" : "text-red-400"}>
                      {verificationResult.isVerified ? "✓ Valid" : "✗ Invalid"}
                    </div>
                    
                    {verificationResult.gridVerified !== undefined && (
                      <>
                        <div className="text-gray-400">Grid Verification:</div>
                        <div className={verificationResult.gridVerified ? "text-green-400" : "text-red-400"}>
                          {verificationResult.gridVerified ? "✓ Valid" : "✗ Invalid"}
                        </div>
                      </>
                    )}
                    
                    {verificationResult.payoutVerified !== undefined && (
                      <>
                        <div className="text-gray-400">Payout Verification:</div>
                        <div className={verificationResult.payoutVerified ? "text-green-400" : "text-red-400"}>
                          {verificationResult.payoutVerified ? "✓ Valid" : "✗ Invalid"}
                        </div>
                      </>
                    )}
                  </div>
                  
                  {/* Show error if verification failed */}
                  {!verificationResult.isVerified && verificationResult.error && (
                    <div className="mt-2 text-xs text-red-400">
                      Error: {verificationResult.error}
                    </div>
                  )}
                </div>
              )}
              
              {/* Verification hash section */}
              {selectedSpin.verificationHash && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="text-xs font-medium text-gray-300">Verification Hash</h4>
                    <button 
                      onClick={() => copyToClipboard(selectedSpin.verificationHash)}
                      className="text-xs text-blue-400 hover:text-blue-300 flex items-center"
                    >
                      <DocumentDuplicateIcon className="h-3 w-3 mr-1" />
                      Copy
                    </button>
                  </div>
                  <div className="bg-gray-900/80 p-2 rounded border border-gray-800">
                    <p className="text-xs font-mono text-blue-400 break-all">
                      {selectedSpin.verificationHash}
                    </p>
                  </div>
                </div>
              )}
              
              {/* Raw quantum numbers */}
              {(selectedSpin.quantumRandomness || verificationResult?.quantumRandomness) && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="text-xs font-medium text-gray-300">Raw Quantum Numbers</h4>
                    <button 
                      onClick={() => copyToClipboard(JSON.stringify(selectedSpin.quantumRandomness || verificationResult.quantumRandomness))}
                      className="text-xs text-blue-400 hover:text-blue-300 flex items-center"
                    >
                      <DocumentDuplicateIcon className="h-3 w-3 mr-1" />
                      Copy
                    </button>
                  </div>
                  <div className="bg-gray-900/80 p-2 rounded border border-gray-800 max-h-24 overflow-y-auto">
                    <p className="text-xs font-mono text-green-400 break-all">
                      {JSON.stringify(selectedSpin.quantumRandomness || verificationResult.quantumRandomness)}
                    </p>
                  </div>
                </div>
              )}
              
              {/* Verification actions */}
              <div className="flex flex-col sm:flex-row gap-2 mt-4">
                {!verificationResult && !selectedSpin.isVerified && (
                  <button 
                    onClick={() => verifySpin(selectedSpin)}
                    className={`flex-1 py-2 px-4 rounded-lg text-xs font-medium flex items-center justify-center ${
                      verifyingInProgress
                        ? "bg-blue-900 text-blue-300 cursor-wait"
                        : "bg-blue-600 hover:bg-blue-500 text-white"
                    }`}
                    disabled={verifyingInProgress}
                  >
                    {verifyingInProgress ? (
                      <>
                        <svg className="animate-spin mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Verifying...
                      </>
                    ) : (
                      <>
                        <BeakerIcon className="h-4 w-4 mr-1.5" />
                        Verify Quantum Spin
                      </>
                    )}
                  </button>
                )}
                
                {selectedSpin.verificationUrl && (
                  <a 
                    href={selectedSpin.verificationUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex-1 py-2 px-4 bg-gray-700 hover:bg-gray-600 text-blue-300 rounded-lg text-xs font-medium flex items-center justify-center"
                  >
                    <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-1.5" />
                    Verify on External Source
                  </a>
                )}
              </div>
              
              {/* Direct SHA-256 Hash calculator link */}
              <div className="mt-4 pt-4 border-t border-gray-700 text-center">
                <a 
                  href="https://emn178.github.io/online-tools/sha256.html" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-xs text-blue-400 hover:text-blue-300 flex items-center justify-center"
                >
                  <ArrowTopRightOnSquareIcon className="h-3 w-3 mr-1" />
                  Verify Hash Online with SHA-256 Calculator
                </a>
              </div>
            </div>
          </div>
        )}
        
        {/* Spin history list view */}
        {!selectedSpin && !loading && (
          <>
            {spins.length === 0 ? (
              <div className="text-center py-8">
                <div className="flex justify-center mb-4">
                  <BeakerIcon className="h-12 w-12 text-gray-600" />
                </div>
                <h3 className="text-gray-400 text-sm font-medium mb-2">No Quantum Spins Yet</h3>
                <p className="text-gray-500 text-xs">Play some spins with Quantum RNG enabled to build your verification history.</p>
              </div>
            ) : (
              <>
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-sm font-medium text-gray-300">Your Quantum Spins</h3>
                  <button 
                    onClick={fetchSpinHistory} 
                    className="text-xs text-blue-400 hover:text-blue-300 flex items-center"
                  >
                    <ArrowPathIcon className="h-3 w-3 mr-1" />
                    Refresh
                  </button>
                </div>
                
                <div className="space-y-2 mb-4">
                  {spins.map(spin => (
                    <div 
                      key={spin._id || spin.id} 
                      className="bg-gray-800/50 hover:bg-gray-800/80 p-3 rounded-lg border border-gray-700 cursor-pointer transition-colors"
                      onClick={() => setSelectedSpin(spin)}
                    >
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <div>
                          <div className="text-xs text-gray-400">Date</div>
                          <div className="text-sm text-gray-200">
                            {formatDate(spin.timestamp || spin.date || spin.createdAt).split(',')[0]}
                          </div>
                        </div>
                        
                        <div>
                          <div className="text-xs text-gray-400">Bet</div>
                          <div className="text-sm text-gray-200">{formatCurrency(spin.amount || spin.betAmount)}</div>
                        </div>
                        
                        <div>
                          <div className="text-xs text-gray-400">Result</div>
                          <div className={`text-sm font-medium ${spin.result === 'win' ? 'text-green-400' : 'text-red-400'}`}>
                            {spin.result === 'win' ? 'WIN' : 'LOSE'}
                          </div>
                        </div>
                        
                        <div>
                          <div className="text-xs text-gray-400">Quantum Source</div>
                          <div className="text-sm text-cyan-400">{spin.quantumSource || 'Unknown'}</div>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-700">
                        <div className="text-xs text-gray-400">
                          {spin.isVerified ? (
                            <span className="flex items-center text-green-400">
                              <CheckCircleIcon className="h-3 w-3 mr-1" />
                              Verified
                            </span>
                          ) : (
                            <span className="text-gray-400">Verification Pending</span>
                          )}
                        </div>
                        
                        <div className="text-xs text-blue-400">View Details →</div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Pagination */}
                {pagination.totalPages > 1 && (
                  <div className="flex items-center justify-between border-t border-gray-700 pt-4">
                    <div className="text-xs text-gray-400">
                      Page {page} of {pagination.totalPages} ({pagination.total} spins)
                    </div>
                    <div className="flex space-x-2">
                      <button 
                        onClick={prevPage}
                        disabled={page <= 1}
                        className={`p-1.5 rounded-lg text-xs ${
                          page <= 1 ? 'text-gray-600 cursor-not-allowed' : 'text-blue-400 hover:bg-gray-800'
                        }`}
                      >
                        <ChevronLeftIcon className="h-4 w-4" />
                      </button>
                      <button 
                        onClick={nextPage}
                        disabled={page >= pagination.totalPages}
                        className={`p-1.5 rounded-lg text-xs ${
                          page >= pagination.totalPages ? 'text-gray-600 cursor-not-allowed' : 'text-blue-400 hover:bg-gray-800'
                        }`}
                      >
                        <ChevronRightIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Help text at the bottom */}
                <div className="text-center mt-6 text-xs text-gray-500">
                  Each quantum spin is cryptographically hashed and can be independently verified
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
